import React from 'react';
import { Calendar } from 'lucide-react';

/**
 * Compact date-range field group with optional quick presets.
 *
 * Stays a controlled component: the parent owns the `from` / `to` strings
 * (always in ISO `YYYY-MM-DD` so they paste straight into the existing
 * backend query parameters — NO date semantic changes per the TZ).
 *
 * Props:
 *   from           — string YYYY-MM-DD
 *   to             — string YYYY-MM-DD
 *   onChange       — ({ from, to }) => void
 *   label          — default "Date"
 *   showPresets    — boolean, default true
 *   className      — extra wrapper classes (the field group already spans
 *                    `col-span-1 sm:col-span-2`; override if needed)
 *   testidPrefix   — default "daterange"
 */
export default function DateRangeFilter({
  from = '',
  to = '',
  onChange,
  label = 'Date',
  showPresets = true,
  className = '',
  testidPrefix = 'daterange',
  presetLabels = {},
}) {
  const setRange = (f, t) => onChange({ from: f, to: t });

  // Quick-preset helpers. All compute YYYY-MM-DD in local timezone to
  // match the existing backend's date filtering (uses ISO date strings,
  // not datetimes).
  const fmt = (d) => d.toISOString().slice(0, 10);
  const today = () => new Date();
  const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d;
  };
  const presets = [
    { key: 'today',     label: presetLabels.today    || 'Today',      apply: () => setRange(fmt(today()), fmt(today())) },
    { key: '7days',     label: presetLabels.d7       || '7 days',     apply: () => setRange(fmt(daysAgo(7)),  fmt(today())) },
    { key: '30days',    label: presetLabels.d30      || '30 days',    apply: () => setRange(fmt(daysAgo(30)), fmt(today())) },
    { key: 'this_month',label: presetLabels.this_month || 'This month', apply: () => {
      const t = today();
      const first = new Date(t.getFullYear(), t.getMonth(), 1);
      setRange(fmt(first), fmt(t));
    }},
  ];

  return (
    <div className={className} data-filter="date-range" data-testid={`${testidPrefix}-group`}>
      <label className="flex items-center gap-1 text-[10px] font-medium text-slate-500 mb-0.5 uppercase tracking-wide">
        <Calendar className="w-3 h-3" />
        {label}
      </label>
      <div className="flex gap-1">
        <input
          type="date"
          value={from}
          onChange={(e) => setRange(e.target.value, to)}
          aria-label={`${label} from`}
          placeholder="From"
          className="flex-1 min-w-0 h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
          data-testid={`${testidPrefix}-from`}
        />
        <input
          type="date"
          value={to}
          onChange={(e) => setRange(from, e.target.value)}
          aria-label={`${label} to`}
          placeholder="To"
          className="flex-1 min-w-0 h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
          data-testid={`${testidPrefix}-to`}
        />
      </div>
      {showPresets && (
        <div className="flex flex-wrap gap-1 mt-1">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={p.apply}
              className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-600 hover:bg-cyan-100 hover:text-cyan-700 transition-colors"
              data-testid={`${testidPrefix}-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
